import React, { useEffect, useMemo, useRef, useState } from 'react';
import { bool, func, number, oneOf, oneOfType, shape, string } from 'prop-types';
import {
  Alert,
  Box,
  Button,
  FormControl,
  FormHelperText,
  IconButton,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import ErrorRoundedIcon from '@mui/icons-material/ErrorRounded';
import * as R from 'ramda';
import { useUserRolePermissions } from 'hooks';
import { useGetProductsQuery } from '../../../customerAuthorizationsEndpoints';
import AddProductsSection from './AddProductsSection';
import ProductsGrid from './ProductsGrid';
import {
  getAuthTypeLabel,
  getProductId,
  getVolumeError,
  isValidProductAuthType,
  needsAuthorizationVolume,
  needsRenewalVolume,
  PRODUCT_AUTH_TYPE_OPTIONS,
  sanitizeVolumeInput,
} from './productRules';

const NO_PRODUCTS_WARNING =
  'There are no products configured for this Product Specific authorization. At least one product is required to enable loading for this customer.';

const warningAlertSx = {
  '& .MuiAlert-icon': {
    alignItems: 'center',
    color: 'error.main',
    display: 'flex',
    mr: 1,
    py: 0,
  },
  '& .MuiAlert-message': {
    alignItems: 'center',
    display: 'flex',
    py: 0,
  },
  alignItems: 'center',
  backgroundColor: 'error.light',
  color: 'error.main',
};

const VolumeCell = ({ ariaLabel, disabled, onChange, value }) => {
  const error = disabled ? '' : getVolumeError(value);
  return (
    <TextField
      disabled={disabled}
      error={Boolean(error)}
      fullWidth
      helperText={error || null}
      onChange={event => onChange(sanitizeVolumeInput(event.target.value))}
      required
      size="small"
      slotProps={{
        formHelperText: { sx: { mt: 0, mx: 0 } },
        htmlInput: { 'aria-label': ariaLabel, inputMode: 'numeric' },
      }}
      sx={{ my: 0.5 }}
      value={R.defaultTo('', value)}
    />
  );
};

VolumeCell.propTypes = {
  ariaLabel: string.isRequired,
  disabled: bool.isRequired,
  onChange: func.isRequired,
  value: oneOfType([number, string]),
};

VolumeCell.defaultProps = {
  value: undefined,
};

const ProductsTabContent = ({ customerAuth, mode, setCustomerAuth }) => {
  const { currentAffiliate = {}, isProductLoadingManage } = useUserRolePermissions();
  const { customerIdName: companyCustomerIdName, dataCompanyId } = currentAffiliate;
  const isEditMode = mode === 'edit';
  const isProductSpecific = customerAuth.authorizationType === 'SPECIFIC';
  const canManage = isEditMode && isProductLoadingManage && isProductSpecific;
  const canEditFields = canManage;
  const [isAddSectionOpen, setIsAddSectionOpen] = useState(false);
  const [hasDismissedEmptyAddSection, setHasDismissedEmptyAddSection] = useState(false);
  const [newProductIds, setNewProductIds] = useState([]);
  const [selectedProductIds, setSelectedProductIds] = useState([]);
  const previousAuthorizationKeyRef = useRef(null);
  const products = useMemo(() => customerAuth.products ?? [], [customerAuth.products]);
  const terminals = useMemo(() => customerAuth.terminals ?? [], [customerAuth.terminals]);
  const authorizationKey = customerAuth?.customerAuthorizationId ?? customerAuth?.customerIdName;
  const customerIdName = customerAuth.customerId ?? customerAuth.customerIdName;
  const showNoProductsWarning =
    isProductSpecific &&
    R.not(R.isNil(customerAuth.products)) &&
    products.length === 0 &&
    selectedProductIds.length === 0;

  // Non-Product Specific auths show every product eligible at the configured terminal(s).
  const params = {
    customerIdName,
    supplier: { companyCustomerIdName, dataCompanyId },
    terminals: R.map(R.prop('terminalId'), terminals),
  };
  const { data: eligibleProducts = [], isFetching: isFetchingEligible } = useGetProductsQuery(
    { params },
    { skip: R.or(isProductSpecific, R.isEmpty(terminals)) }
  );

  useEffect(() => {
    const previousAuthorizationKey = previousAuthorizationKeyRef.current;

    if (previousAuthorizationKey && previousAuthorizationKey !== authorizationKey) {
      setIsAddSectionOpen(false);
      setHasDismissedEmptyAddSection(false);
      setNewProductIds([]);
      setSelectedProductIds([]);
    }

    previousAuthorizationKeyRef.current = authorizationKey;
  }, [authorizationKey]);

  useEffect(() => {
    if (!isProductSpecific) {
      setSelectedProductIds([]);
      setIsAddSectionOpen(false);
      setHasDismissedEmptyAddSection(false);
    }
  }, [isProductSpecific]);

  // The add-products section is open by default when zero products are configured.
  useEffect(() => {
    if (canManage && showNoProductsWarning && !hasDismissedEmptyAddSection) {
      setIsAddSectionOpen(true);
    }
  }, [canManage, hasDismissedEmptyAddSection, showNoProductsWarning]);

  // If a product's current Auth Type from the BE is not valid, clear it so user must pick a valid one.
  useEffect(() => {
    if (isProductSpecific && Array.isArray(customerAuth.products)) {
      const hasInvalidAuthType = customerAuth.products.some(
        product => product.authorizationType && !isValidProductAuthType(product.authorizationType)
      );
      if (hasInvalidAuthType) {
        setCustomerAuth(previous => ({
          ...previous,
          products: (previous.products ?? []).map(product =>
            product.authorizationType && !isValidProductAuthType(product.authorizationType)
              ? { ...product, authorizationType: '' }
              : product
          ),
        }));
      }
    }
  }, [customerAuth.products, isProductSpecific, setCustomerAuth]);

  const updateProduct = (productId, changes) => {
    setCustomerAuth(previous => ({
      ...previous,
      products: (previous.products ?? []).map(product =>
        getProductId(product) === productId ? { ...product, ...changes } : product
      ),
    }));
  };

  const handleAuthTypeChange = (productId, authorizationType) => {
    updateProduct(productId, {
      authorizationType,
      authorizationVolume: authorizationType === 'ONELOAD' ? 1 : undefined,
      renewalVolume: undefined,
    });
  };

  const handleRemoveProduct = productId => {
    setNewProductIds(previous => previous.filter(id => id !== productId));
    setSelectedProductIds([]);
    setHasDismissedEmptyAddSection(false);
    if (products.length === 1) {
      setIsAddSectionOpen(true);
    }
    setCustomerAuth(previous => ({
      ...previous,
      products: (previous.products ?? []).filter(product => getProductId(product) !== productId),
    }));
  };

  const handleAddProducts = addedProducts => {
    setNewProductIds(previous => [...new Set([...previous, ...addedProducts.map(getProductId)])]);
    setCustomerAuth(previous => ({
      ...previous,
      products: [
        ...(previous.products ?? []),
        ...addedProducts.map(product => ({
          ...product,
          authorizationType: 'UNLIMITED',
          authorizationVolume: undefined,
          renewalVolume: undefined,
        })),
      ],
    }));
    setSelectedProductIds([]);
    setIsAddSectionOpen(false);
    setHasDismissedEmptyAddSection(false);
  };

  const handleCancelAddProducts = () => setHasDismissedEmptyAddSection(true);

  const addProductButtonSx = {
    borderRadius: 1,
    height: 40,
    minHeight: 40,
    minWidth: 155,
    whiteSpace: 'nowrap',
    ...(isAddSectionOpen
      ? {
          '&:hover': { backgroundColor: 'secondary.dark' },
          backgroundColor: 'secondary.main',
          color: 'common.white',
        }
      : {
          '&:hover': {
            backgroundColor: 'secondary.dark',
            color: 'common.white',
          },
          backgroundColor: 'common.white',
          border: '1px solid',
          borderColor: 'secondary.main',
          color: 'secondary.main',
        }),
  };

  const addProductButton = (
    <Button
      aria-controls="add-products-section"
      aria-expanded={isAddSectionOpen}
      color="success"
      onClick={() => setIsAddSectionOpen(previous => !previous)}
      startIcon={<AddRoundedIcon />}
      sx={addProductButtonSx}
      variant={isAddSectionOpen ? 'contained' : 'outlined'}
    >
      Add Product
    </Button>
  );

  const specificColumns = useMemo(
    () => [
      {
        align: 'left',
        field: 'authorizationType',
        getApplyQuickFilterFn: searchValue => {
          if (!searchValue) return null;
          return cellParams => {
            const authType = cellParams?.row?.authorizationType || '';
            const label = getAuthTypeLabel(authType);
            const target = `${authType} ${label}`.toLowerCase();
            return target.includes(String(searchValue).toLowerCase());
          };
        },
        headerName: 'Auth Type *',
        minWidth: 150,
        renderCell: ({ row }) => {
          const isInvalid = !isValidProductAuthType(row.authorizationType);
          const value = isInvalid ? '' : row.authorizationType;
          const showError = canEditFields && isInvalid;
          return (
            <FormControl error={showError} fullWidth size="small" sx={{ my: 0.5 }}>
              <Select
                disabled={!canEditFields}
                inputProps={{ 'aria-label': `auth type for ${row.productName}` }}
                onChange={event => handleAuthTypeChange(row.id, event.target.value)}
                required
                size="small"
                value={value}
              >
                {PRODUCT_AUTH_TYPE_OPTIONS.map(({ label, value: optionValue }) => (
                  <MenuItem key={optionValue} value={optionValue}>
                    {label}
                  </MenuItem>
                ))}
              </Select>
              {showError && <FormHelperText sx={{ mt: 0, mx: 0 }}>Required</FormHelperText>}
            </FormControl>
          );
        },
        resizable: true,
        sortable: true,
        valueGetter: cellParams =>
          isValidProductAuthType(cellParams?.row?.authorizationType)
            ? getAuthTypeLabel(cellParams.row.authorizationType)
            : '',
        width: 160,
      },
      {
        align: 'center',
        field: 'authorizationVolume',
        getApplyQuickFilterFn: () => null,
        headerAlign: 'center',
        headerName: 'Auth Vol *',
        minWidth: 120,
        renderCell: ({ row }) => {
          if (!needsAuthorizationVolume(row.authorizationType)) {
            return <span>-</span>;
          }
          const isOneLoad = row.authorizationType === 'ONELOAD';
          const volumeValue = isOneLoad ? row.authorizationVolume ?? 1 : row.authorizationVolume;
          return (
            <VolumeCell
              ariaLabel={`authorization volume for ${row.productName}`}
              disabled={!canEditFields || isOneLoad}
              onChange={value =>
                updateProduct(row.id, {
                  authorizationVolume:
                    value === '' ? undefined : value.length > 14 ? value : Number(value),
                })
              }
              value={volumeValue}
            />
          );
        },
        resizable: true,
        sortable: false,
        width: 160,
      },
      {
        align: 'center',
        field: 'renewalVolume',
        getApplyQuickFilterFn: () => null,
        headerAlign: 'center',
        headerName: 'Renewal *',
        minWidth: 120,
        renderCell: ({ row }) =>
          needsRenewalVolume(row.authorizationType) ? (
            <VolumeCell
              ariaLabel={`renewal volume for ${row.productName}`}
              disabled={!canEditFields}
              onChange={value =>
                updateProduct(row.id, {
                  renewalVolume:
                    value === '' ? undefined : value.length > 14 ? value : Number(value),
                })
              }
              value={row.renewalVolume}
            />
          ) : (
            <span>-</span>
          ),
        resizable: true,
        sortable: false,
        width: 160,
      },
      ...(canManage
        ? [
            {
              align: 'center',
              field: 'remove',
              getApplyQuickFilterFn: () => null,
              headerAlign: 'center',
              headerName: 'Remove',
              renderCell: ({ row }) => (
                <IconButton
                  aria-label={`remove ${row.productName}`}
                  onClick={() => handleRemoveProduct(row.id)}
                  size="small"
                >
                  <DeleteOutlineRoundedIcon fontSize="small" />
                </IconButton>
              ),
              resizable: false,
              sortable: false,
              width: 90,
            },
          ]
        : []),
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [canEditFields, canManage]
  );

  if (!isProductSpecific) {
    const authTypeLabel = getAuthTypeLabel(customerAuth.authorizationType);
    return (
      <Stack spacing={2}>
        <Alert icon={<ErrorRoundedIcon color="error" />} severity="error" sx={warningAlertSx}>
          <Typography color="inherit" component="p" sx={{ lineHeight: 1.5, m: 0 }} variant="body2">
            {authTypeLabel && <strong>{authTypeLabel}</strong>}
            {authTypeLabel ? ': this customer' : 'This customer'} can load all products you have
            active Self or My authorizations for at the configured terminal(s). No other
            product-level restrictions apply.
          </Typography>
        </Alert>
        <ProductsGrid
          darkHeader
          gridHeight="clamp(240px, calc(100vh - 420px), 520px)"
          gridProps={{ loading: isFetchingEligible }}
          products={eligibleProducts}
          quickFilterAriaLabel="Filter products"
          quickFilterLabel="Filter products"
          quickFilterPlaceholder="Filter products..."
        />
      </Stack>
    );
  }

  return (
    <Stack spacing={2}>
      {showNoProductsWarning && (
        <Alert icon={<ErrorRoundedIcon color="error" />} severity="error" sx={warningAlertSx}>
          {NO_PRODUCTS_WARNING}
        </Alert>
      )}
      {canManage && showNoProductsWarning && (
        <Stack alignItems="flex-end">{addProductButton}</Stack>
      )}
      <ProductsGrid
        belowQuickFilterContent={
          canManage ? (
            <Box sx={{ mb: isAddSectionOpen ? 2 : 0 }}>
              <AddProductsSection
                customerIdName={customerIdName}
                existingProducts={products}
                isOpen={isAddSectionOpen}
                onAddProducts={handleAddProducts}
                onCancel={handleCancelAddProducts}
                selectedProductIds={selectedProductIds}
                setIsOpen={setIsAddSectionOpen}
                setSelectedProductIds={setSelectedProductIds}
                terminals={terminals}
              />
            </Box>
          ) : null
        }
        darkHeader
        extraColumns={specificColumns}
        gridHeight="clamp(240px, calc(100vh - 420px), 520px)"
        gridProps={{ rowHeight: 64 }}
        hideGrid={showNoProductsWarning}
        hideQuickFilter={showNoProductsWarning}
        newProductIds={newProductIds}
        products={products}
        quickFilterAriaLabel="Filter products"
        quickFilterLabel="Filter products"
        quickFilterPlaceholder="Filter products..."
        topRightContent={!showNoProductsWarning && canManage ? addProductButton : null}
      />
    </Stack>
  );
};

ProductsTabContent.propTypes = {
  customerAuth: shape({}).isRequired,
  mode: oneOf(['edit', 'view']).isRequired,
  setCustomerAuth: func.isRequired,
};

export default ProductsTabContent;
